import {Collection} from 'backbone';
import TagModel, {tagEquals} from '../../models/common/tagModel';
import {SystemTagModel} from '../../models/common/SystemTagModel';
import {RestrictionTagModel} from '../../models/common/RestrictionTagModel';
import {CaliasTagModel} from '../../models/common/CaliasTagModel';
import {UserGeneratedTagModel} from '../../models/common/UserGeneratedTagModel';
import {MajorStoryTagModel} from '../../models/common/MajorStoryTagModel';
import {AiTagModel} from '../../models/common/AiTagModel';
import {T} from 'ramda';
import {
    SYSTEM_DIMENSIONS,
    RESTRICTION_DIMENSIONS,
    SOURCE_USERGENERATED,
    SOURCE_AI,
    SOURCE_CALAIS,
    MAJOR_STORY_NAME,
    MAJOR_STORY_DIMENSION,
} from '../../constants/tags';

export const geoDimensions = new Set(RESTRICTION_DIMENSIONS);
export const systemTagsDimensions = new Set(SYSTEM_DIMENSIONS);

export const isRestrictionTag = tag => geoDimensions.has(tag.get('dimension'));

const TagCollection = Collection.extend({
    comparator: 'order',
    modelId({name, dimension}) {
        return name + dimension;
    },
    model(attrs, options) {
        const nextOrder = Math.max(...(this.models ?? []).map(model => model.get('order') ?? -1)) + 1;
        const sourceText = (attrs.source ?? '').toUpperCase();

        // special case logic for major story
        if (attrs.name === MAJOR_STORY_NAME && attrs.dimension === MAJOR_STORY_DIMENSION) {
            return new MajorStoryTagModel(attrs, options);
        }

        // check the source text first
        switch (sourceText) {
            case SOURCE_AI:
                attrs.order = attrs.order || nextOrder;
                return new AiTagModel(attrs, options);

            case SOURCE_USERGENERATED:
                if (attrs.userGenerated === true) {
                    attrs.order = attrs.order || nextOrder;
                    return new UserGeneratedTagModel(attrs, options);
                }

            case SOURCE_CALAIS:
                attrs.order = attrs.order || nextOrder;
                return new CaliasTagModel(attrs, options);
        }

        // lastly fallback for other ill-defined behavior
        if (attrs.dimension === 'AI Generated') {
            attrs.order = attrs.order || nextOrder;
            return new AiTagModel(attrs, options);
        }

        if (attrs.userGenerated === true) {
            attrs.order = attrs.order || nextOrder;
            return new UserGeneratedTagModel(attrs, options);
        }

        if (systemTagsDimensions.has(attrs.dimension)) {
            return new SystemTagModel(attrs, options);
        }

        if (geoDimensions.has(attrs.dimension)) {
            return new RestrictionTagModel(attrs, options);
        }

        if (attrs.weight < 1) {
            attrs.order = attrs.order || nextOrder;
            return new CaliasTagModel(attrs, options);
        }
        // fallback case
        return new TagModel(attrs, options);
    },
    initialize() {
        this.on('add remove', this.updateOrder, this);
    },
    /**
     * Handle the ordering logic, only order TagModels, not SystemTagModels
     *
     * @returns {void}
     */
    updateOrder() {
        let index = 0;
        this.forEach(
            (tag) => {
                if (tag instanceof TagModel && tag.getIsOrderable()) {
                    tag.set({order: index});
                    index += 1;
                }
            }
        );
    },
    /**
     * Determine if the tag already exist in the collection
     *
     * @param {TagModel|Object} tag
     * @returns {boolean}
     */
    contains(tag) {
        return this.some(tagEquals(tag));
    },
    /**
     * Conditional add, only if it's not already in the collection
     *
     * @param {TagModel|Object} tag
     * @returns {TagCollection}
     */
    addIfNotContains(tag) {
        if (!this.contains(tag)) {
            this.add(tag);
        }

        return this;
    },
    canAddTag: T,
    /**
     * Wrapper for adding tags to the collection
     * Need to investigate how order is managed here, will likely need to be removed
     *
     * @param {Object[]} tags - tags to merge into the collection
     * @param {boolean} [__force=false] - force merge?
     * @returns {TagCollection}
     */
    merge(tags, __force = false) {
        if (tags instanceof Collection) {
            this.add(
                tags.sortBy('order')
                    .map(({order: __order, ...rest}) => rest),
                {merge: true}
            );
        } else if (Array.isArray(tags) && tags.length) {
            this.add(
                tags.sort(
                    (a, b) => {
                        return (a.order ?? 0) - (b.order ?? 0);
                    }
                ),
                {merge: true}
            );
        }

        return this;

        /*
        const _tags = this.parse(tags);
        const _collection = new TagCollection(_tags);
        const [oTags, nTags] = partition(tag => this.contains(tag))(_collection.models);
        oTags.forEach(tag => {
            if (tag.get('userGenerated')) {
                // if userGenerated is set to true, this should override any previous attribute
                this.add(tag, {merge: true});
            } else {
                const _tag = this.get(tag);
                const _weight = _tag.get('weight');
                if (_weight !== 1 || force) {
                    _tag.set({weight: tag.get('weight')});
                }
            }
        });
        this.add(nTags);
        return this;
*/
    },
    /**
     * Return a list of tags that the user can interact with (or see)
     *
     * @returns {TModel[]}
     */
    getVisibleTags() {
        return this.reject(
            tag => tag instanceof SystemTagModel
                || tag instanceof RestrictionTagModel
                || tag instanceof MajorStoryTagModel
                || tag.get('included') === false,
        );
    },
    /**
     * Returns a list of tags that the user cannot see, the complement of visible tags
     *
     * @returns {TModel[]}
     */
    getInvisibleTags() {
        return this.filter(
            tag => tag instanceof SystemTagModel
                || tag instanceof RestrictionTagModel
                || tag instanceof MajorStoryTagModel
                || tag.get('included') === false,
        );
    },
    /**
     * Returns a list of only the system tags
     *
     * @returns {TModel[]}
     */
    getSystemTags() {
        return this.filter(
            tag => tag instanceof SystemTagModel
        );
    },
    /**
     * Complement to getSystemTags, only returns those that are not system tags
     * (but also rejects restriction tags for some reason)
     *
     * @returns {TModel[]}
     */
    getNonSystemTags() {
        return this.reject(
            tag => tag instanceof SystemTagModel
                || tag instanceof RestrictionTagModel
        );
    },
    /**
     * Return a list of tags that were generated by the user
     *
     * @returns {TModel[]}
     */
    getUserGenerated() {
        return this.filter(
            tag => tag instanceof UserGeneratedTagModel
        );
    },
    /**
     * Returns a list of restriction tags
     *
     * @returns {TModel[]}
     */
    getGeoRestrictions() {
        return this.filter(
            tag => tag instanceof RestrictionTagModel
        );
    },
    /**
     * Returns a list of open-calias tags
     *
     * @returns {TModel[]}
     */
    getSuggested() {
        return this.filter(
            tag => tag instanceof CaliasTagModel
        );
    },
    getExcluded() {
        return this.where({included: false});
    },
    parse(input = []) {
        let parsed = input || [];
        if (parsed instanceof TagCollection) {
            parsed = parsed.toJSON();
        }
        if ('function' === typeof parsed.toJSON) {
            parsed = parsed.toJSON();
        }
        if (!Array.isArray(parsed)) {
            parsed = [parsed];
        }
        return parsed
            .sort((a, b) => {
                return (a.order ?? 0) - (b.order ?? 0);
            })
            .reduce(
                (acc, item) => {
                    if (!Object.prototype.hasOwnProperty.call(item, 'order')) {
                        item.order = (acc.at(-1)?.order ?? -1) + 1;
                    }
                    acc.push(item);
                    return acc;
                },
                []
            );
    },
});

export default TagCollection;

export const TopicTagCollection = TagCollection.extend({
    maxTags: 3,
    isValidTag(tag) {
        return !systemTagsDimensions.has(tag.get('dimension'));
    },
    canAddTag(tag) {
        return this.length < this.maxTags && this.isValidTag(tag) && !this.contains(tag);
    },
});

export const HashTagCollection = TagCollection.extend({
    maxTags: 1,
    canAddTag(tag) {
        const dim = tag.get('dimension');
        return this.length < this.maxTags && dim === 'HashTag' && !this.contains(tag);
    },
});
